// Native avatar-photo picking with a CLASSIFIED result — the fix for the
// denied-permission case: with photo-library permission denied, Camera.getPhoto
// rejects and the old call sites silently console.log'd it, so tapping
// "From Photos" did nothing, forever (Apple never re-asks). Callers now get an
// explicit status and show the "access is off → Settings" popup instead of
// dead air. Shared by ProfilePage and onboarding Step2Identity so the two
// paths can't drift apart.
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { t } from "../i18n";

export type AvatarPickResult =
  | { status: "ok"; dataUrl: string }
  | { status: "cancelled" }
  | { status: "denied" }
  | { status: "error" };

export async function pickAvatarPhotoNative(): Promise<AvatarPickResult> {
  try {
    // Force JPEG + smaller size for server compat (HEIC/Web huge size).
    const photo = await Camera.getPhoto({
      quality: 70,
      allowEditing: false,
      source: CameraSource.Prompt,
      resultType: CameraResultType.Base64,
      presentationStyle: "popover",
      saveToGallery: false,
      webUseInput: true,
      // Founder decision: the in-app sheet offers exactly these two, so label
      // them the way iOS does ("Take Photo" / "Photo Library").
      promptLabelHeader: t("avatarPhoto.addAPhoto"),
      promptLabelPhoto: t("avatarPhoto.photoLibrary"),
      promptLabelPicture: t("avatarPhoto.takePhoto"),
    });
    if (!photo?.base64String) return { status: "cancelled" };
    return { status: "ok", dataUrl: `data:image/jpeg;base64,${photo.base64String}` };
  } catch (err: any) {
    const msg = String(err?.message || err || "").toLowerCase();
    // The plugin rejects with "User cancelled photos app" (and similar) when
    // the talent backs out on purpose — silence is correct there.
    if (msg.includes("cancel")) return { status: "cancelled" };
    // Anything else: check whether a permission is the blocker (verified via
    // TCC logs: denied photo access rejects right here).
    try {
      const perms = await Camera.checkPermissions();
      if (perms.photos === "denied" || perms.camera === "denied") {
        return { status: "denied" };
      }
    } catch {
      /* checkPermissions itself failed — fall through to generic error */
    }
    console.error("[avatar-photo] pick failed:", err);
    return { status: "error" };
  }
}
